import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, shareReplay, take } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

const GOOGLE_MAPS_API_URL = 'https://maps.googleapis.com/maps/api/js';
const GOOGLE_MAPS_LIBRARIES = 'places,geometry';
const GOOGLE_MAPS_LANGUAGE = 'en';
const GOOGLE_MAPS_VERSION = 'weekly';
const GOOGLE_MAPS_CALLBACK_PARAM = 'callback';

/**
 * Loads the Google Maps JavaScript API on demand instead of from `index.html`,
 * so the API key is only requested on screens that actually use Maps, Places,
 * Geocoder or Distance Matrix.
 *
 * The request is made once per application lifetime and shared between all callers.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private apiLoaded$: Observable<boolean> | null = null;

  /**
   * Whether `google.maps` is already available on `window`.
   */
  get isLoaded(): boolean {
    return !!(window as unknown as { google?: { maps?: unknown } }).google?.maps;
  }

  /**
   * Loads the Google Maps JavaScript API and completes once it is available on `window`.
   * Subsequent calls reuse the same request.
   *
   * @returns Observable emitting `true` when the API is usable, `false` when loading failed
   */
  load(): Observable<boolean> {
    if (!this.apiLoaded$) {
      this.apiLoaded$ = this.isLoaded ? of(true) : this.requestApi();
    }

    return this.apiLoaded$;
  }

  /**
   * Runs a callback as soon as the Google Maps API is usable, or immediately when it already is.
   * The callback is skipped when the API could not be loaded.
   *
   * @param onLoaded - Work that depends on the `google.maps` namespace
   * @returns Subscription the caller is responsible for cleaning up
   */
  whenLoaded(onLoaded: () => void): Subscription {
    return this.load()
      .pipe(
        take(1),
        filter((isLoaded: boolean) => isLoaded),
      )
      .subscribe(() => onLoaded());
  }

  /**
   * Defers work until the Google Maps API is available.
   *
   * Intended as a guard at the top of a method that needs `google.maps`: pass a callback that
   * re-enters the same method, then return early when this returns `true`.
   *
   * @param retry - Callback that re-runs the deferred work once the API is ready
   * @param subscriptions - Container that owns the pending subscription
   * @returns Whether the work was deferred and the caller should return early
   */
  deferUntilLoaded(retry: () => void, subscriptions: Subscription): boolean {
    if (this.isLoaded) {
      return false;
    }

    subscriptions.add(this.whenLoaded(retry));

    return true;
  }

  /**
   * Requests the Maps script through JSONP so the browser executes it and the
   * observable completes only after `google.maps` is ready.
   *
   * @returns Observable emitting `true` on success, `false` when the script could not be loaded
   */
  private requestApi(): Observable<boolean> {
    const url = `${GOOGLE_MAPS_API_URL}?key=${environment.googleMapsKey}&libraries=${GOOGLE_MAPS_LIBRARIES}&language=${GOOGLE_MAPS_LANGUAGE}&v=${GOOGLE_MAPS_VERSION}`;

    return this.httpClient.jsonp(url, GOOGLE_MAPS_CALLBACK_PARAM).pipe(
      map(() => true),
      catchError((error: unknown) => {
        // Every map on every screen depends on this request, so a failure must not stay silent.
        console.error('Google Maps API failed to load. Maps, Places, Geocoder and Distance Matrix are unavailable.', error);

        return of(false);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }
}
